import PageHeader from '@components/common/PageHeader';
import { Button, Tabs } from '@mantine/core';
import { IconSettings } from '@tabler/icons-react';
import { useState, type FC } from 'react';
import { useSearchParams } from 'react-router';

import type { RebalancePlan, RebalanceScope } from '@api/rebalance';
import RebalanceOverview from './RebalanceOverview';
import RebalancePlansTab from './RebalancePlansTab';
import RebalanceSettingsModal from './RebalanceSettingsModal';

type TabValue = 'overview' | 'plans';

/** `?scope=NODE:<id>` | `NETWORK` | `WAREHOUSE` — the plan builder's scope picker as a URL param. */
const scopeFromParam = (raw: string | null): RebalanceScope | null => {
    if (!raw) return null;
    if (raw.startsWith('NODE:')) {
        const nodeId = raw.slice(5);
        return nodeId ? { level: 'NODE', nodeId } : null;
    }
    return raw === 'NETWORK' || raw === 'WAREHOUSE' ? { level: raw } : null;
};

const RebalanceContainer: FC = () => {
    // /rebalance?tab=plans&scope=NODE:<id>&autocompute=1 — a Home plan card opens the builder
    // on its scope and runs it (the same thing "Run rebalance" on the overview does).
    const [searchParams] = useSearchParams();
    const deepLinkScope = scopeFromParam(searchParams.get('scope'));
    const deepLinkPlans = searchParams.get('tab') === 'plans';
    const deepLinkAutoCompute = searchParams.get('autocompute') === '1';
    const [activeTab, setActiveTab] = useState<TabValue>(deepLinkPlans ? 'plans' : 'overview');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [openPlan, setOpenPlan] = useState<RebalancePlan | null>(null);
    /** Scope preset for a NEW plan, set by "Run rebalance" / "Rebalance within" on the overview. */
    const [newPlanScope, setNewPlanScope] = useState<RebalanceScope | null>(deepLinkPlans ? deepLinkScope : null);
    const [autoCompute, setAutoCompute] = useState(deepLinkPlans && deepLinkAutoCompute);

    const handleSelectPlan = (plan: RebalancePlan) => {
        setOpenPlan(plan);
        setNewPlanScope(null);
        setAutoCompute(false);
        setActiveTab('plans');
    };

    const handleRunRebalance = (scope: RebalanceScope) => {
        setOpenPlan(null);
        setNewPlanScope(scope);
        setAutoCompute(false);
        setActiveTab('plans');
    };

    return (
        <div className="flex h-full w-full flex-col page-shell">
            <PageHeader
                icon="⚖️"
                title="Rebalance"
                subtitle="Optimise stock distribution across internal warehouses"
                actions={
                    <Button
                        variant="action"
                        radius="md"
                        size="sm"
                        leftSection={<IconSettings size={16} />}
                        onClick={() => setSettingsOpen(true)}
                    >
                        Settings
                    </Button>
                }
            />

            <Tabs
                value={activeTab}
                onChange={v => { setActiveTab(v as TabValue); setOpenPlan(null); setNewPlanScope(null); setAutoCompute(false); }}
                mt="md"
                style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
                <Tabs.List>
                    <Tabs.Tab value="overview">Overview</Tabs.Tab>
                    <Tabs.Tab value="plans">Plans</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="overview" pt="md" style={{ flex: 1, overflow: 'auto' }}>
                    <RebalanceOverview
                        onRunRebalance={handleRunRebalance}
                        onSelectPlan={handleSelectPlan}
                    />
                </Tabs.Panel>

                <Tabs.Panel
                    value="plans"
                    pt="md"
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
                >
                    <RebalancePlansTab
                        key={newPlanScope ? `new-${newPlanScope.level}-${newPlanScope.nodeId ?? ''}` : 'plans'}
                        initialPlan={openPlan}
                        initialScope={newPlanScope}
                        autoCompute={autoCompute}
                    />
                </Tabs.Panel>
            </Tabs>

            <RebalanceSettingsModal
                opened={settingsOpen}
                onClose={() => setSettingsOpen(false)}
            />
        </div>
    );
};

export default RebalanceContainer;
